import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import type { AdapterHealth } from '@familyhub/shared';
import { db, storesByName} from '../db';
import { dismissResyncNotice, lastSyncedAt, resyncNotice, sync, type ResyncNotice } from '../sync/engine';
import { logout } from '../session';
import { describe as describeRejection, discard, isRetryable, retry } from '../db/rejections';

/**
 * Status (ADAPTERS §5.3).
 *
 * "Surface adapter health **in the app**, not only in logs — the person who
 * needs to know that Ekoplaza broke is the one about to go shopping." The list
 * already flags a broken store on its group; this is where you find out *why*,
 * and when the household last had a working connection.
 *
 * It is a status screen, not a settings screen. There is nothing to configure:
 * that is the point of ARCHITECTURE §3.3.
 */

const HEALTH_LABEL: Record<AdapterHealth, string> = {
  ok: 'Werkt',
  degraded: 'Wisselend',
  failed: 'Alleen handmatig',
  unsupported: 'Geen live zoeken',
};

function ago(iso: string | undefined): string {
  if (!iso) return 'nooit';
  const seconds = Math.round((Date.now() - Date.parse(iso)) / 1000);
  if (!Number.isFinite(seconds)) return 'nooit';
  if (seconds < 90) return 'zojuist';
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} minuten geleden`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours} uur geleden`;
  return `${Math.round(hours / 24)} dagen geleden`;
}

export function StatusScreen() {
  const [synced, setSynced] = useState<string | undefined>(undefined);
  const stores = useLiveQuery(storesByName, [], []);
  const queued = useLiveQuery(() => db.outbox.count(), [], 0);

  /**
   * Refused changes, each named in the terms the person used. A rejection they
   * cannot identify tells them only that something was lost.
   */
  const rejections = useLiveQuery(async () => {
    const rows = await db.rejections.orderBy('at').reverse().toArray();
    return Promise.all(
      rows.map(async (row) => ({ ...row, what: await describeRejection(row.mutation) })),
    );
  }, [], []);

  useEffect(() => {
    void lastSyncedAt().then(setSynced);
  }, [queued]);

  /**
   * Shown when a full resync took things off this phone's list (REVIEW G4).
   * They were bought while it was away, the tombstones had already been purged
   * server-side, and the only honest thing to do is say so — items vanishing
   * from a shared list with no explanation is how people stop trusting it.
   */
  const [resync, setResync] = useState<ResyncNotice | null>(null);
  useEffect(() => {
    void resyncNotice().then((n) => setResync(n ?? null));
  }, [synced]);

  return (
    <>
      <header className="topbar">
        <Link className="back btn btn-quiet" to="/boodschappen">
          ‹ Terug
        </Link>
        <h1>Status</h1>
      </header>

      <main className="stack">
        <p className="section-label">Synchronisatie</p>
        <div className="card">
          <div className="row">
            <span className="row-main">
              <span className="row-name">Laatst gesynchroniseerd</span>
              <span className="row-sub">
                {synced === undefined
                  ? // Distinguished from "a while ago": a phone that has never
                    // synced is a different situation from one that synced on
                    // Tuesday, and only one of them needs explaining.
                    'Nog niet — deze telefoon heeft de server nog niet bereikt'
                  : queued === 0
                    ? 'Alles hier staat op de server'
                    : `Nog ${queued} ${queued === 1 ? 'wijziging' : 'wijzigingen'} te versturen`}
              </span>
            </span>
            <span className="badge">{ago(synced)}</span>
          </div>
        </div>
        <button className="btn" onClick={() => void sync().then(() => lastSyncedAt().then(setSynced))}>
          Nu synchroniseren
        </button>

        {rejections.length > 0 && (
          <>
            <p className="section-label">Wijzigingen die niet opgeslagen zijn</p>
            <div className="card">
              {rejections.map((rejection) => (
                <div className="row row-top" key={rejection.id}>
                  <span className="row-main">
                    <span className="row-name">{rejection.what}</span>
                    {/* The server's reason for refusing a change, in full: this
                        is the only place a person can find out what was lost. */}
                    <span className="row-sub wraps">{rejection.message}</span>
                  </span>
                  {/* Only offered where trying again could actually work —
                      a retry button on a permanent failure is a lie. */}
                  {isRetryable(rejection.code) && (
                    <button className="btn btn-quiet" onClick={() => void retry(rejection.id)}>
                      Opnieuw proberen
                    </button>
                  )}
                  <button
                    className="btn btn-quiet"
                    aria-label={`${rejection.what} weggooien`}
                    onClick={() => void discard(rejection.id)}
                  >
                    Weggooien
                  </button>
                </div>
              ))}
            </div>
            <p className="row-sub">
              Deze zijn door de server geweigerd en staan dus niet op je lijst. Wat je toch nog
              wilt hebben, voeg je opnieuw toe.
            </p>
          </>
        )}

        {resync && (
          <div className="card" style={{ padding: 12 }}>
            <p style={{ margin: '0 0 8px' }}>
              <strong>Je lijst is opnieuw opgebouwd</strong>
              <br />
              <span className="row-sub">
                Deze telefoon had niet meer gesynchroniseerd sinds {ago(resync.last_synced ?? undefined)},
                en dat is langer dan het huishouden bijhoudt wat er gekocht is.{' '}
                {resync.removed === 1 ? 'Eén item' : `${resync.removed} items`} die al gekocht{' '}
                {resync.removed === 1 ? 'was' : 'waren'} zijn verwijderd. Niets van wat je op deze
                telefoon hebt toegevoegd is verloren.
              </span>
            </p>
            <button className="btn btn-quiet" onClick={() => void dismissResyncNotice().then(() => setResync(null))}>
              Duidelijk
            </button>
          </div>
        )}

        <p className="section-label">Winkels</p>
        <div className="card">
          {stores.map((store) => (
            <div className="row row-top" key={store.id}>
              <span className="row-main">
                <span className="row-name">{store.name}</span>
                {/* The note is the actual reason, not a euphemism for one —
                    and it wraps, because a reason cut off mid-word is not one. */}
                <span className="row-sub wraps">
                  {store.health_note ?? 'Live zoeken werkt'}
                </span>
              </span>
              {/* Aisle order is the one thing §6.5 calls household configuration,
                  so a link to set it belongs here — including for a store with
                  nothing on the list yet, which the list itself cannot reach. */}
              <Link className="btn btn-quiet" to={`/boodschappen/aisles/${store.code}`}>
                Schapvolgorde
              </Link>
              <span className="badge">{HEALTH_LABEL[store.health]}</span>
            </div>
          ))}
          {stores.length === 0 && (
            <div className="row">
              <span className="row-sub">Nog niet gesynchroniseerd.</span>
            </div>
          )}
        </div>
        <p className="row-sub">
          Zonder live zoeken voeg je items met de hand toe — prijzen kan zo'n winkel niet tonen.
        </p>

        <button
          className="btn btn-danger"
          onClick={() => {
            // Everything local goes with it: a shared or lost phone must not
            // keep the household's list behind a signed-out screen.
            void logout().then(() => window.location.replace('/'));
          }}
        >
          Uitloggen{queued > 0 ? ` (${queued} ${queued === 1 ? 'wijziging' : 'wijzigingen'} niet verstuurd)` : ''}
        </button>
        {queued > 0 && (
          <p className="row-sub" style={{ color: 'var(--danger)' }}>
            Nu uitloggen gooit die wijzigingen weg. Synchroniseer eerst als dat kan.
          </p>
        )}
      </main>
    </>
  );
}
